<template>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="$vuetify.breakpoint.mobile ? '98vw' : '960'"
    content-class="dds-invoice-dialog"
  >
    <v-card>
      <v-toolbar color="primary" elevation="0" class="no-print">
        <v-toolbar-title class="white--text">{{ $t('dueDiligence.viewInvoice') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" text class="mr-2" :loading="downloading" @click="downloadPdf">
          <v-icon :style="{ color: 'white !important' }" left small>mdi-download</v-icon>
          {{ $t('dueDiligence.downloadInvoice') }}
        </v-btn>
        <v-btn color="white" text class="mr-2" @click="printInvoice">
          <v-icon :style="{ color: 'white !important' }" left small>mdi-printer</v-icon>
          {{ $t('deforestation.print') }}
        </v-btn>
        <v-btn color="white" icon @click="closeModal">
          <v-icon style="color:white !important">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 invoice-scroll">
        <div id="dds-invoice-document" ref="invoiceDocument" class="invoice-document">
          <div class="invoice-logo-bar">
            <img
              src="/img/id/ptsi_logo.webp"
              alt="PT Surveyor Indonesia"
              class="invoice-logo"
            />
          </div>

          <div class="invoice-top">
            <div>
              <div class="invoice-brand">{{ issuerName }}</div>
              <div class="invoice-subtitle">{{ $t('dueDiligence.ddsReviewFeeInvoice') }}</div>
            </div>
            <!-- <div class="invoice-status-chip" :class="statusClass">
              {{ paymentStatusLabel }}
            </div> -->
          </div>

          <!-- 1. Invoice Header -->
          <section class="invoice-section">
            <h3 class="section-title">{{ $t('dueDiligence.invoiceHeader') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.invoiceNumber') }}</span>
                <span class="value">{{ invoiceNumber }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
                <span class="value">{{ ddsReference }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.invoiceDate') }}</span>
                <span class="value">{{ invoiceDate }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.paymentStatus') }}</span>
                <span class="value">{{ paymentStatusLabel }}</span>
              </div>
            </div>
          </section>

          <!-- 2. Payer Information -->
          <section class="invoice-section">
            <h3 class="section-title">{{ $t('dueDiligence.payerInformation') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.exporterName') }}</span>
                <span class="value">{{ payer.name }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.exporterCompanyId') }}</span>
                <span class="value">{{ payer.companyId }}</span>
              </div>
              <div class="field full">
                <span class="label">{{ $t('dueDiligence.exporterContact') }}</span>
                <span class="value">{{ payer.contact }}</span>
              </div>
            </div>
          </section>

          <!-- 3. DDS / Product Information -->
          <section class="invoice-section">
            <h3 class="section-title">{{ $t('dueDiligence.ddsProductInformation') }}</h3>
            <table class="invoice-table">
              <thead>
                <tr>
                  <th>{{ $t('deforestation.product') }}</th>
                  <th class="text-right">{{ $t('dueDiligence.quantityNetMass') }}</th>
                  <th class="text-right">{{ $t('dueDiligence.unit') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ productName }}</td>
                  <td class="text-right">{{ formattedNetMass }}</td>
                  <td class="text-right">{{ $t('dueDiligence.unitTon') }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- 4. Pricing Information -->
          <section class="invoice-section">
            <h3 class="section-title">{{ $t('dueDiligence.pricingInformation') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.pricePerTon') }}</span>
                <span class="value">{{ formattedPricePerTon }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.currency') }}</span>
                <span class="value">{{ currency }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.calculatedAmount') }}</span>
                <span class="value">{{ formattedAmount }}</span>
              </div>
            </div>
          </section>

          <!-- 5. Payment Information -->
          <section class="invoice-section highlight">
            <h3 class="section-title">{{ $t('dueDiligence.paymentInformation') }}</h3>
            <div class="total-row">
              <span>{{ $t('dueDiligence.totalAmountPayable') }}</span>
              <strong>{{ formattedAmount }}</strong>
            </div>
            <div class="section-grid mt-3">
              <div v-if="ddsPaymentBankDetailsVisible" class="field full">
                <span class="label">{{ $t('dueDiligence.bankAccountInstructions') }}</span>
                <span class="value">{{ $t('dueDiligence.bankTransferPaymentInstructions') }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.paymentDueStatus') }}</span>
                <span class="value">{{ paymentDueStatusLabel }}</span>
              </div>
            </div>
          </section>

          <!-- 6. Bank Details (toggle via showDdsPaymentBankDetails in utils.js) -->
          <section v-if="ddsPaymentBankDetailsVisible" class="invoice-section">
            <h3 class="section-title">{{ $t('dueDiligence.bankDetails') }}</h3>
            <div class="bank-box">
              <div><strong>{{ $t('dueDiligence.bankName') }}:</strong> {{ bankDetails.bankName || '-' }}</div>
              <div><strong>{{ $t('dueDiligence.accountName') }}:</strong> {{ bankDetails.accountName || '-' }}</div>
              <div><strong>{{ $t('dueDiligence.accountNumber') }}:</strong> {{ bankDetails.accountNumber || '-' }}</div>
            </div>
          </section>

          <!-- 7. Submitted Payment Proof -->
          <section ref="proofSection" class="invoice-section proof-section no-print">
            <h3 class="section-title">{{ $t('dueDiligence.submittedPaymentProof') }}</h3>
            <template v-if="hasSubmittedReceipt">
              <p class="proof-hint">{{ submittedByHint }}</p>
              <div class="section-grid">
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.receiptSubmittedBy') }}</span>
                  <span class="value">{{ submittedByLabel }}</span>
                </div>
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.receiptSubmittedAt') }}</span>
                  <span class="value">{{ submittedAtLabel }}</span>
                </div>
                <div class="field full" v-if="payment && payment.transaction_note">
                  <span class="label">{{ $t('dueDiligence.paymentProofNote') }}</span>
                  <span class="value">{{ payment.transaction_note }}</span>
                </div>
              </div>
              <div class="proof-actions mt-3">
                <v-btn
                  depressed
                  color="primary"
                  :loading="loadingReceipt"
                  @click="openReceipt"
                >
                  <v-icon :style="{ color: 'white !important' }" left small>mdi-file-eye-outline</v-icon>
                  {{ $t('dueDiligence.openSubmittedReceipt') }}
                </v-btn>
              </div>
            </template>
            <p v-else class="proof-empty">{{ $t('dueDiligence.receiptNotSubmittedYet') }}</p>
          </section>

          <div class="invoice-footer">
            <p>{{ $t('dueDiligence.invoiceFooterNote') }}</p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4 no-print">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="closeModal">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import html2pdf from 'html2pdf.js';
import printJS from 'print-js';
import moment from 'moment';
import { normalizeDdsStatus, showDdsPaymentBankDetails as isDdsPaymentBankDetailsEnabled, resolveDdsExporterPayerFields } from '@/utils';
import S3UploadService from '@/_services/S3UploadService';

export default {
  name: 'ViewInvoiceModal',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    item: {
      type: Object,
      default: null,
    },
    bankDetails: {
      type: Object,
      default: () => ({
        bankName: '',
        accountName: '',
        accountNumber: '',
      }),
    },
    payerInfo: {
      type: Object,
      default: () => ({
        name: '-',
        companyId: '-',
        contact: '-',
      }),
    },
    issuerName: {
      type: String,
      default: 'PT Surveyor Indonesia',
    },
  },
  data() {
    return {
      ddsPaymentBankDetailsVisible: isDdsPaymentBankDetailsEnabled(),
      downloading: false,
      loadingReceipt: false,
    };
  },
  computed: {
    payment() {
      return this.item?.payment || null;
    },
    invoiceNumber() {
      if (this.payment?.invoice_number) return this.payment.invoice_number;
      if (this.item?.id && this.payment?.id) {
        return `INV-${this.item.id}-${this.payment.id}`;
      }
      if (this.item?.id) return `INV-${this.item.id}`;
      return '-';
    },
    ddsReference() {
      const eudr = this.item?.EUDRReferenceNumber;
      const internal = this.item?.internalReferenceNumber;
      if (eudr && internal) return `${eudr} / ${internal}`;
      return eudr || internal || '-';
    },
    invoiceDate() {
      const raw = this.payment?.invoice_generated_at
        || this.payment?.updatedAt
        || this.payment?.createdAt
        || this.item?.updatedAt;
      if (!raw) return moment().format('DD MMM YYYY');
      return moment(raw).format('DD MMM YYYY');
    },
    paymentStatus() {
      return normalizeDdsStatus(this.payment?.status);
    },
    paymentStatusLabel() {
      const map = {
        pending: this.$t('dueDiligence.paymentDue'),
        invoice_generated: this.$t('dueDiligence.invoiceGeneratedStatus'),
        reported: this.$t('dueDiligence.paymentPendingVerification'),
        verified: this.$t('dueDiligence.paymentVerified'),
        rejected: this.$t('dueDiligence.paymentRejected'),
      };
      return map[this.paymentStatus] || (this.payment?.status || '-');
    },
    paymentDueStatusLabel() {
      if (['verified'].includes(this.paymentStatus)) {
        return this.$t('dueDiligence.paymentPaid');
      }
      if (['reported'].includes(this.paymentStatus)) {
        return this.$t('dueDiligence.paymentPendingVerification');
      }
      if (['rejected'].includes(this.paymentStatus)) {
        return this.$t('dueDiligence.paymentRejected');
      }
      return this.$t('dueDiligence.paymentDue');
    },
    hasSubmittedReceipt() {
      return !!(this.payment?.receipt_s3_key);
    },
    currentUserId() {
      return this.$store?.getters?.getUser?.id ?? null;
    },
    isSubmittedByCurrentUser() {
      if (!this.payment?.payer_cf_id || !this.currentUserId) return false;
      return Number(this.payment.payer_cf_id) === Number(this.currentUserId);
    },
    submittedExporter() {
      const payerCfId = this.payment?.payer_cf_id;
      if (!payerCfId || !this.item?.ddsReportExporters?.length) return null;
      return this.item.ddsReportExporters.find(
        (exporter) => Number(exporter.exporter_cf_id) === Number(payerCfId)
      ) || null;
    },
    submittedByLabel() {
      if (!this.hasSubmittedReceipt) return '-';
      if (this.isSubmittedByCurrentUser) {
        return this.$t('dueDiligence.receiptSubmittedByYou');
      }
      const exporter = this.submittedExporter;
      if (exporter) {
        const resolved = resolveDdsExporterPayerFields(exporter, this.item?.companyID);
        if (resolved.contact !== '-') return resolved.contact;
        if (resolved.name !== '-') return resolved.name;
      }
      if (this.payerInfo?.contact && this.payerInfo.contact !== '-') return this.payerInfo.contact;
      if (this.payerInfo?.name && this.payerInfo.name !== '-') return this.payerInfo.name;
      return this.$t('dueDiligence.receiptSubmittedByAnotherExporter');
    },
    submittedByHint() {
      return this.isSubmittedByCurrentUser
        ? this.$t('dueDiligence.paymentProofHintYou')
        : this.$t('dueDiligence.paymentProofHintOther');
    },
    submittedAtLabel() {
      const raw = this.payment?.reported_at;
      if (!raw) return '-';
      return moment(raw).format('DD MMM YYYY, HH:mm');
    },
    statusClass() {
      return {
        'status-due': ['pending', 'invoice_generated'].includes(this.paymentStatus),
        'status-pending': this.paymentStatus === 'reported',
        'status-paid': this.paymentStatus === 'verified',
        'status-rejected': this.paymentStatus === 'rejected',
      };
    },
    payer() {
      return {
        name: this.payerInfo?.name || '-',
        companyId: this.payerInfo?.companyId || this.item?.companyID || '-',
        contact: this.payerInfo?.contact || '-',
      };
    },
    productName() {
      const detail = this.item?.product_detail;
      if (!detail) return this.item?.product || '-';
      return detail.code ? `${detail.code} - ${detail.name}` : detail.name;
    },
    netMassTons() {
      if (this.payment?.net_mass_tons_snapshot != null) {
        return Number(this.payment.net_mass_tons_snapshot);
      }
      const kg = this.item?.productNetMass;
      if (kg == null || isNaN(kg)) return null;
      return Number(kg) / 1000;
    },
    formattedNetMass() {
      if (this.netMassTons == null) return '-';
      return this.netMassTons.toLocaleString(undefined, { maximumFractionDigits: 4 });
    },
    currency() {
      return this.payment?.currency || 'IDR';
    },
    pricePerTon() {
      if (this.payment?.price_per_ton_snapshot != null) {
        return Number(this.payment.price_per_ton_snapshot);
      }
      if (this.payment?.amount != null && this.netMassTons) {
        return Number(this.payment.amount) / this.netMassTons;
      }
      return null;
    },
    formattedPricePerTon() {
      if (this.pricePerTon == null) return '-';
      return `${this.currency} ${this.pricePerTon.toLocaleString()}`.trim();
    },
    formattedAmount() {
      if (this.payment?.amount == null) return '-';
      return `${this.currency} ${Number(this.payment.amount).toLocaleString()}`.trim();
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    async openReceipt() {
      if (!this.payment?.receipt_s3_key) return;
      this.loadingReceipt = true;
      try {
        const url = await S3UploadService.getFileUrl(this.payment.receipt_s3_key);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error opening payment receipt:', error);
        this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
      } finally {
        this.loadingReceipt = false;
      }
    },
    printInvoice() {
      const element = this.$refs.invoiceDocument;
      if (!element) return;
      printJS({
        printable: 'dds-invoice-document',
        type: 'html',
        documentTitle: this.invoiceNumber || 'DDS-Invoice',
        scanStyles: false,
        css: null,
        style: `
          body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; }
          .invoice-document { padding: 24px; }
          .invoice-logo-bar { margin-bottom: 16px; }
          .invoice-logo { height: 56px; width: auto; max-width: 240px; object-fit: contain; display: block; }
          .invoice-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; border-bottom: 2px solid #0d47a1; padding-bottom: 16px; }
          .invoice-brand { font-size: 22px; font-weight: 700; color: #0d47a1; }
          .invoice-subtitle { font-size: 13px; color: #555; margin-top: 4px; }
          .invoice-status-chip { padding: 6px 12px; border-radius: 4px; font-size: 12px; font-weight: 600; border: 1px solid #ccc; }
          .invoice-section { margin-bottom: 18px; }
          .section-title { font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: #0d47a1; margin: 0 0 10px; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; }
          .section-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; }
          .field { display: flex; flex-direction: column; }
          .field.full { grid-column: 1 / -1; }
          .label { font-size: 11px; color: #666; margin-bottom: 2px; }
          .value { font-size: 14px; font-weight: 600; }
          .invoice-table { width: 100%; border-collapse: collapse; }
          .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 8px 10px; font-size: 13px; }
          .invoice-table th { background: #f5f7fa; text-align: left; }
          .text-right { text-align: right; }
          .highlight { background: #f8fafc; padding: 12px; border-radius: 6px; }
          .total-row { display: flex; justify-content: space-between; align-items: center; font-size: 16px; }
          .bank-box { background: #f8f9fa; padding: 12px; border-radius: 6px; line-height: 1.7; font-size: 13px; }
          .invoice-footer { margin-top: 28px; font-size: 11px; color: #777; border-top: 1px solid #eee; padding-top: 12px; }
          .invoice-section, .invoice-table, .invoice-table tr { page-break-inside: avoid; break-inside: avoid; }
          .no-print { display: none !important; }
        `,
      });
    },
    async downloadPdf() {
      const element = this.$refs.invoiceDocument;
      if (!element) return;
      this.downloading = true;
      const proofSection = this.$refs.proofSection;
      const proofSectionPrevDisplay = proofSection ? proofSection.style.display : null;
      if (proofSection) proofSection.style.display = 'none';
      try {
        const filename = `${this.invoiceNumber || 'DDS-Invoice'}.pdf`;
        await html2pdf()
          .set({
            margin: [0.4, 0.4, 0.4, 0.4],
            filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'], avoid: ['.invoice-section', '.invoice-table', 'tr'] },
          })
          .from(element)
          .save();
      } catch (error) {
        console.error('Error downloading invoice PDF:', error);
        this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
      } finally {
        if (proofSection) proofSection.style.display = proofSectionPrevDisplay || '';
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-scroll {
  max-height: 75vh;
  overflow-y: auto;
  background: #fff;
}

.invoice-document {
  background: #fff;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 24px 28px;
  box-shadow: none;
  color: #1a1a1a;
  box-sizing: border-box;
}

.invoice-logo-bar {
  margin-bottom: 16px;
}

.invoice-logo {
  height: 56px;
  width: auto;
  max-width: 240px;
  object-fit: contain;
  display: block;
}

.invoice-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  border-bottom: 2px solid #0d47a1;
  padding-bottom: 16px;
}

.invoice-brand {
  font-size: 22px;
  font-weight: 700;
  color: #0d47a1;
}

.invoice-subtitle {
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}

.invoice-status-chip {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-due {
  background: #fff3e0;
  color: #e65100;
}

.status-pending {
  background: #e3f2fd;
  color: #1565c0;
}

.status-paid {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-rejected {
  background: #ffebee;
  color: #c62828;
}

.invoice-section {
  margin-bottom: 18px;
}

.section-title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0d47a1;
  margin: 0 0 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
}

.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
}

.field {
  display: flex;
  flex-direction: column;
}

.field.full {
  grid-column: 1 / -1;
}

.label {
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.value {
  font-size: 14px;
  font-weight: 600;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
}

.invoice-table th,
.invoice-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  font-size: 13px;
}

.invoice-table th {
  background: #f5f7fa;
  text-align: left;
}

.text-right {
  text-align: right;
}

.highlight {
  background: #f8fafc;
  padding: 12px;
  border-radius: 6px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.bank-box {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  line-height: 1.7;
  font-size: 13px;
}

.invoice-footer {
  margin-top: 28px;
  font-size: 11px;
  color: #777;
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.proof-section {
  background: #f3f8ff;
  border: 1px solid #d6e4f5;
  border-radius: 8px;
  padding: 14px;
}

.proof-hint {
  font-size: 13px;
  color: #455a64;
  margin: 0 0 12px;
}

.proof-empty {
  font-size: 13px;
  color: #78909c;
  margin: 0;
  font-style: italic;
}

.proof-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 600px) {
  .invoice-document {
    margin: 0;
    padding: 16px;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }

  .invoice-top {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
